import { Controller } from "stimulus";

// Odometer-style rolling number: each digit is a vertical 0-9 strip that slides
// to its target digit, so the value rolls/flips like Twitch's viewer count.
// Progressive enhancement - the server renders the plain number; we build the
// digit columns on connect and animate on change.
export default class extends Controller {
  static values = {
    count: Number,
    duration: { type: Number, default: 600 },
  };

  connect() {
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.element.style.display = "inline-flex";
    this.element.style.lineHeight = "1";
    this.setValue(this.countValue, false);
  }

  countValueChanged(value, previous) {
    if (previous === undefined) return; // initial render handled by connect()
    this.setValue(value, !this.reducedMotion);
  }

  setValue(value, animate) {
    const digits = String(Math.max(0, Math.round(value || 0))).split("").map(Number);
    if (animate && this.digits && digits.length === this.digits.length) {
      digits.forEach((digit, i) => this.slide(this.strips[i], digit, true));
    } else {
      this.rebuild(digits, animate && Boolean(this.digits));
    }
    this.digits = digits;
    this.element.setAttribute("aria-label", digits.join(""));
  }

  // Digit count changed (or first paint): rebuild columns. When animating, shared
  // columns start from the old value (right-aligned) so they still roll.
  rebuild(digits, animate) {
    const previous = this.digits || [];
    const pad = digits.length - previous.length;
    this.element.textContent = "";
    this.strips = [];

    digits.forEach((digit, i) => {
      const startDigit = animate && i - pad >= 0 ? previous[i - pad] : digit;
      const { column, strip } = this.buildColumn(startDigit);
      this.element.appendChild(column);
      this.strips.push(strip);
    });

    if (animate) {
      void this.element.offsetHeight; // flush start positions before transitioning
      digits.forEach((digit, i) => this.slide(this.strips[i], digit, true));
    }
  }

  slide(strip, digit, animate) {
    strip.style.transition = animate
      ? `transform ${this.durationValue}ms cubic-bezier(0.22, 1, 0.36, 1)`
      : "none";
    strip.style.transform = `translateY(-${digit * 10}%)`;
  }

  buildColumn(digit) {
    const column = document.createElement("span");
    column.setAttribute("aria-hidden", "true");
    column.style.display = "inline-block";
    column.style.height = "1em";
    column.style.overflow = "hidden";

    const strip = document.createElement("span");
    strip.style.display = "block";
    strip.style.willChange = "transform";
    for (let i = 0; i <= 9; i += 1) {
      const cell = document.createElement("span");
      cell.style.display = "block";
      cell.style.height = "1em";
      cell.textContent = String(i);
      strip.appendChild(cell);
    }
    this.slide(strip, digit, false);
    column.appendChild(strip);
    return { column, strip };
  }
}
